import { BaseComponent } from '../../types';
import { Card } from '../Card/Card';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface CenterSectionProps extends BaseComponent {}

export function CenterSection({ className }: CenterSectionProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section
      className={`w-full flex justify-center items-center px-6 ${className || ''}`}
      style={{
        minHeight: '781px',
        background: 'var(--Background, #020816)',
      }}
    >
      <div
        ref={ref}
        className={`transition-all duration-700 ease-out ${
          isVisible
            ? 'opacity-100 transform translate-y-0 scale-100'
            : 'opacity-0 transform translate-y-8 scale-95'
        }`}
      >
        {/* Desktop Layout */}
        <div className="hidden lg:block relative">
          {/* Outer Ring */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '208px',
              height: '208px',
              borderRadius: '104px',
              border: '1px solid rgba(59, 130, 246, 0.20)',
            }}
          />

          {/* Background Circle */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '160px',
              height: '160px',
              borderRadius: '80px',
              border: '1px solid rgba(56, 189, 248, 0.30)',
              background:
                'radial-gradient(70.71% 70.71% at 50% 50%, rgba(56, 189, 248, 0.30) 0%, rgba(59, 130, 246, 0.20) 50%, rgba(59, 130, 246, 0) 100%)',
            }}
          />

          {/* Inner Circle */}
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '25px',
              background:
                'radial-gradient(70.71% 70.71% at 50% 50%, rgba(56, 189, 248, 0.90) 0%, rgba(59, 130, 246, 0.70) 100%)',
              boxShadow:
                '0 0 30px 0 rgba(56, 189, 248, 0.60), 0 0 20px 0 rgba(255, 255, 255, 0.20) inset',
            }}
          />

          {/* Vertical Curved Line - extending upward from center circle edge */}
          <svg
            className="absolute"
            style={{
              bottom: 'calc(50% + 25px)',
              left: 'calc(50% - 180px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="182"
            height="306"
            viewBox="0 0 182 306"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M180.75 304.472C180.75 174.306 137.893 70.1723 0.750244 0.750122"
              stroke="url(#paint0_linear_1103_4303)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4303"
                x1="0.750244"
                y1="0.750122"
                x2="267.174"
                y2="158.645"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Curved Line - extending from center circle */}
          <svg
            className="absolute"
            style={{
              bottom: 'calc(50% - -20.5px)',
              left: 'calc(50% + 25px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="448"
            height="123"
            viewBox="0 0 448 123"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M0.750244 122.239C129.322 61.4946 300.75 26.7835 446.465 0.750122"
              stroke="url(#paint0_linear_1103_4304)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4304"
                x1="0.750244"
                y1="0.750122"
                x2="62.3988"
                y2="226.924"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Second Curved Line - extending from center circle downward */}
          <svg
            className="absolute"
            style={{
              top: 'calc(50% - 8.5px)',
              left: 'calc(50% + 2px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="448"
            height="167"
            viewBox="0 0 448 167"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M0.75 0.750122C129.321 87.5279 300.75 148.272 446.464 165.628"
              stroke="url(#paint0_linear_1103_4306)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4306"
                x1="0.75"
                y1="0.750122"
                x2="108.05"
                y2="290.814"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Upper Left Curved Line - extending from center circle upward left */}
          <svg
            className="absolute"
            style={{
              bottom: 'calc(50% + 20px)',
              right: 'calc(50% + 25px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="448"
            height="193"
            viewBox="0 0 448 193"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M446.465 191.661C317.893 130.917 146.465 44.1391 0.750244 0.750244"
              stroke="url(#paint0_linear_1103_4302)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4302"
                x1="0.750244"
                y1="0.750244"
                x2="138.942"
                y2="323.381"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Left Curved Line - extending from center circle leftward */}
          <svg
            className="absolute"
            style={{
              top: 'calc(50% + 20px)',
              right: 'calc(50% + 25px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="448"
            height="115"
            viewBox="0 0 448 115"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M446.464 0.750244C317.893 44.1391 146.464 113.561 0.75 113.561"
              stroke="url(#paint0_linear_1103_4305)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4305"
                x1="0.75"
                y1="0.750244"
                x2="54.4174"
                y2="212.789"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Downward Curved Line - extending from center circle downward */}
          <svg
            className="absolute"
            style={{
              top: 'calc(50% + 10px)',
              left: 'calc(50% - 65px)',
            }}
            xmlns="http://www.w3.org/2000/svg"
            width="71"
            height="306"
            viewBox="0 0 71 306"
            fill="none"
          >
            <path
              opacity="0.25"
              d="M69.3214 0.75C69.3214 148.272 52.1786 261.083 0.75 304.472"
              stroke="url(#paint0_linear_1103_4307)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1103_4307"
                x1="0.75"
                y1="0.75"
                x2="131.241"
                y2="30.2111"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#38BDF8" stopOpacity="0.6" />
                <stop offset="0.5" stopColor="#3B82F6" stopOpacity="0.5" />
                <stop offset="1" stopColor="#2563EB" stopOpacity="0.4" />
              </linearGradient>
            </defs>
          </svg>

          {/* First Card - positioned on the upward curved line */}
          <Card
            title="Satıcı Yönetimi"
            content="Satıcılarınızı merkezi olarak yönetin ve performanslarını takip edin."
            style={{
              top: 'calc(50% - 350px)',
              left: 'calc(50% - 160px)',
              zIndex: 10,
            }}
          />

          {/* Second Card - positioned on the right side */}
          <Card
            title="Ürün Kataloğu Yönetimi"
            content="Ürünlerinizi kolayca düzenleyin ve stok durumunu kontrol edin."
            style={{
              top: 'calc(50% - 200px)',
              left: 'calc(50% + 200px)',
              zIndex: 10,
            }}
          />

          {/* Third Card - positioned below the second card */}
          <Card
            title="Raporlama ve Analiz"
            content="Detaylı raporlar alın ve iş verilerinizi analiz edin."
            style={{
              top: 'calc(50% + 50px)',
              left: 'calc(50% + 200px)',
              zIndex: 10,
            }}
          />

          {/* Fourth Card - positioned to the left of the third card */}
          <Card
            title="Kampanya ve Promosyon Yönetimi"
            content="Etkili kampanyalar oluşturun ve müşteri sadakatini artırın."
            style={{
              top: 'calc(50% + 150px)',
              left: 'calc(50% - 160px)',
              zIndex: 10,
            }}
          />

          {/* Sixth Card - positioned above the fifth card */}
          <Card
            title="Sipariş ve Lojistik"
            content="Siparişleri takip edin ve lojistik süreçlerinizi optimize edin."
            style={{
              top: 'calc(50% - 230px)',
              left: 'calc(50% - 520px)',
              zIndex: 10,
            }}
          />

          {/* Fifth Card - positioned to the left of the fourth card, above it */}
          <Card
            title="Ürün Kataloğu Yönetimi"
            content="Ürünlerinizi kolayca düzenleyin ve stok durumunu kontrol edin."
            style={{
              top: 'calc(50% + 20px)',
              left: 'calc(50% - 520px)',
              zIndex: 10,
            }}
          />
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden w-full max-w-4xl">
          <div className="grid grid-cols-2 gap-6 justify-items-center">
            <Card
              title="Satıcı Yönetimi"
              content="Satıcıları yönetin ve takip edin."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
            <Card
              title="Ürün Kataloğu Yönetimi"
              content="Ürünleri düzenleyin ve stok kontrolü yapın."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
            <Card
              title="Sipariş ve Lojistik"
              content="Sipariş takibi ve lojistik optimizasyonu."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
            <Card
              title="Raporlama ve Analiz"
              content="Detaylı raporlar ve veri analizi."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
            <Card
              title="Kampanya ve Promosyon Yönetimi"
              content="Kampanya oluşturun ve sadakat artırın."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
            <Card
              title="Ürün Kataloğu Yönetimi"
              content="Ürünleri düzenleyin ve stok kontrolü yapın."
              className="relative"
              style={{ position: 'relative', zIndex: 1 }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
